import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Building2, Target, Users2 } from "lucide-react";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import { siteConfig } from "@/config/siteConfig";

// Simple region grid strategy (chosen over a full SVG world map — cleaner,
// no false-positive of a "distributor map"). Aspirational labels only.
const REGIONS = [
  {
    key: "middle-east",
    name: "Middle East",
    focus: ["UAE (base)", "Saudi Arabia", "Egypt"],
  },
  {
    key: "africa",
    name: "Africa",
    focus: ["North Africa", "East Africa", "West Africa"],
  },
  {
    key: "south-asia",
    name: "South Asia",
    focus: ["Bangladesh", "India (development)", "Pakistan (development)"],
  },
  {
    key: "east-asia",
    name: "East Asia",
    focus: ["Development focus"],
  },
];

const COLUMNS = [
  {
    key: "base",
    Icon: Building2,
    title: "Established Dubai Base",
    body:
      "Commercial and logistics operations coordinated from Dubai, with visibility of shipments to distribution partners and customers.",
  },
  {
    key: "expanding",
    Icon: Target,
    title: "Actively Expanding",
    body:
      "We are actively developing our network across the Middle East, Africa and South Asia — supporting new partners with product, technical and commercial onboarding.",
  },
  {
    key: "partnerships",
    Icon: Users2,
    title: "Local Partnerships",
    body:
      "We work with qualified local partners who bring technical sales capability and market presence in pharmaceutical, packaging or industrial printing.",
  },
];

const GlobalDistribution = () => {
  const path = "/global-distribution";
  const canonical = `${siteConfig.DOMAIN}${path}`;
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "Global Distribution",
    description:
      "International supply from Dubai across the Middle East, Africa and Asia — actively developing our distribution and technical partner network.",
    url: canonical,
  };

  return (
    <>
      <SEO
        rawTitle={`Global Distribution — Middle East, Africa, Asia | ${siteConfig.COMPANY_NAME}`}
        description="International supply from Dubai across the Middle East, Africa and Asia — pharmaceutical printing, coding and production consumables via specialist partners."
        path={path}
        extraSchema={schema}
      />
      <Helmet>
      </Helmet>

      <div className="mx-auto max-w-[1240px] px-6 lg:px-10 pt-8 lg:pt-10">
        <Breadcrumbs
          items={[
            { label: "Home", to: "/" },
            { label: "Global Distribution" },
          ]}
        />
      </div>

      <section
        className="mx-auto max-w-[1240px] px-6 lg:px-10 pt-8 lg:pt-10 pb-12"
        data-testid="gd-hero"
      >
        <p className="eyebrow">International Supply</p>
        <h1
          className="font-heading mt-4 text-[32px] leading-tight lg:text-[46px] lg:leading-[1.05] font-semibold text-[var(--navy)] max-w-4xl"
          data-testid="gd-h1"
        >
          International Supply. Local Market Expertise.
        </h1>
        <p className="mt-6 text-[16.5px] leading-relaxed text-[var(--charcoal)] max-w-3xl">
          Headquartered in Dubai, {siteConfig.COMPANY_NAME} is developing
          commercial and distribution relationships across the Middle East,
          Africa and Asia.
        </p>
      </section>

      {/* Region grid — aspirational, not a distributor map */}
      <section
        className="mx-auto max-w-[1240px] px-6 lg:px-10 py-8"
        data-testid="gd-regions"
      >
        <div className="border border-[var(--border)] rounded-[4px] overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
            {REGIONS.map((r, i) => (
              <div
                key={r.key}
                className={`p-6 lg:p-7 bg-white ${
                  i > 0 ? "md:border-l md:border-[var(--border)]" : ""
                } ${i >= 2 ? "border-t md:border-t-0 lg:border-t-0" : ""}`}
                data-testid={`gd-region-${r.key}`}
              >
                <p
                  className="text-[12px] uppercase tracking-[0.12em] font-semibold"
                  style={{ color: "var(--pharma-blue)" }}
                >
                  Development Focus
                </p>
                <h2 className="font-heading mt-3 text-[19px] font-semibold text-[var(--navy)]">
                  {r.name}
                </h2>
                <ul className="mt-4 space-y-1.5 text-[14px] text-[var(--charcoal)]">
                  {r.focus.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <span
                        className="mt-2 shrink-0 w-1.5 h-1.5 rounded-full"
                        style={{ background: "var(--pharma-blue)" }}
                        aria-hidden="true"
                      />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="border-t border-[var(--border)] px-6 lg:px-7 py-5 bg-[var(--bg-alt)]">
            <p className="text-[13.5px] italic text-[var(--slate)]" data-testid="gd-disclaimer">
              We are actively developing our international distribution network.
              These are target markets and development focus areas — not
              existing exclusive territories.
            </p>
          </div>
        </div>
      </section>

      {/* Three columns */}
      <section
        className="mx-auto max-w-[1240px] px-6 lg:px-10 py-14 lg:py-20 border-t border-[var(--border)] mt-4"
        data-testid="gd-columns"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10">
          {COLUMNS.map(({ key, Icon, title, body }) => (
            <div key={key} data-testid={`gd-column-${key}`}>
              <Icon
                strokeWidth={1.5}
                style={{ width: 36, height: 36, color: "var(--pharma-blue)" }}
                aria-hidden="true"
              />
              <h2 className="font-heading mt-5 text-[19px] font-semibold text-[var(--navy)]">
                {title}
              </h2>
              <p className="mt-3 text-[15px] leading-relaxed text-[var(--slate)]">
                {body}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA band */}
      <section
        className="mx-auto max-w-[1240px] px-6 lg:px-10 pb-20"
        data-testid="gd-cta"
      >
        <div className="border border-[var(--border)] rounded-[4px] p-8 lg:p-12 flex flex-col lg:flex-row items-start lg:items-center gap-6">
          <div className="flex-1">
            <h2 className="font-heading text-xl lg:text-2xl font-semibold text-[var(--navy)]">
              Ready to discuss a market or partnership opportunity?
            </h2>
            <p className="mt-3 text-[15px] text-[var(--charcoal)] max-w-2xl">
              We work with qualified partners who bring technical sales
              capability and coverage in pharmaceutical, packaging or
              industrial printing markets.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/distributors"
              className="btn-primary text-sm"
              data-testid="gd-cta-distributor"
            >
              Become a Distribution Partner
            </Link>
            <Link
              to="/contact"
              className="btn-outline text-sm"
              data-testid="gd-cta-talk"
            >
              Talk to Our Team
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default GlobalDistribution;
